import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators'
import { AssetFile, BoundingBox, Point, SingleAssetUsage } from './asset'
import { store } from '@/store'
import { AppModule, App } from '@/store/modules/app'
import { CustomPuzzle, Asset, ImageRatio, CollageLayers, CollageLayer } from '@/rest/models'
import { AddCustomPuzzleRequest } from '@/rest/apis'
import { Design, Category, Collage } from '@/rest'
import Vue from 'vue'
import i18n from '@/i18n'
import { getProductId } from '@/components/products/framepuzzle/util/partsUtil'

export interface FramePuzzleState {
    parts: string
    giftOption: boolean
    previewAssetId: string
}

export class CollageInfo {
    assetId: number
    boundingBox: { ll: Point, ur: Point }
    rotation: number
    attributesNeedUpdate: boolean

    constructor(
        assetId: number,
        boundingBox: { ll: Point, ur: Point },
        rotation: number,
        attributesNeedUpdate: boolean) {
        this.assetId = assetId
        this.boundingBox = boundingBox
        this.rotation = rotation
        this.attributesNeedUpdate = attributesNeedUpdate
    }

    attributesChanged(newVersion: CollageInfo): boolean {
        if (this.assetId != newVersion.assetId) {
            return true
        }
        if (this.boundingBox.ll[0] != newVersion.boundingBox.ll[0] || this.boundingBox.ll[1] != newVersion.boundingBox.ll[1]) {
            return true
        }
        if (this.boundingBox.ur[0] != newVersion.boundingBox.ur[0] || this.boundingBox.ur[1] != newVersion.boundingBox.ur[1]) {
            return true
        }
        if (this.rotation != newVersion.rotation) {
            return true
        }
        return false
    }
}

export enum PuzzleFormat {
    LANDSCAPE,
    PORTRAIT
}

export enum PuzzleBoxState {
    NONE,
    PAPER_BOX,
    METAL_BOX
}

export enum PuzzleBoxUpdateState {
    UPDATED,
    UPDATING_ASSET_ATTRIBUTES,
    UPDATING_BOX_ASSET
}

export type Ratio = {
    [key in string]: Array<number>
}

export type CollageUsageAndInfo = {
    [key in string]: CollageInfo
}

export type ActiveAsset = {
    [key in string]: AssetFile
}

export type ProductName = {
    [key in string]: string
}

export type NameAndUsage = {
    [key in string]: SingleAssetUsage
}

export type ProductBoxes = {
    parts: string,
    boxes: boolean[]
}

export interface ProductPart {
    part: string,
    price: string
}

export class FramePuzzleTransfer implements CustomPuzzle {

    public sessionId?: string
    public parts: string
    public format: string
    public dru1AssetId: number
    public design: number
    public gift?: boolean
    public boxType: string
    public title: string

    constructor(sessionId: string | undefined, parts: string,
        format: string, design: number, dru1AssetId: number) {
        this.sessionId = sessionId
        this.parts = parts
        this.format = format
        this.design = design
        this.dru1AssetId = dru1AssetId
        this.boxType = "PAPER_BOX"
        this.title = ""
    }

}

@Module({ dynamic: true, store, name: 'framePuzzle' })
class FramePuzzleImpl extends VuexModule implements FramePuzzleState {
    public parts = ''
    public boxDesign = 1
    public boxDesignCategory = 0
    public giftOption = false
    public format = PuzzleFormat.LANDSCAPE
    public userFormat = ''
    public boxState = PuzzleBoxState.NONE
    public puzzleImageAssetId = -1
    public puzzleImageBoundingBox = { ll: [0, 0] as Point, ur: [0, 0] as Point }
    public puzzleImageRotation = 0
    public showOverlayUpload = false
    public availableParts: { value: string; text: string; }[] = []
    public availableBoxes: ProductBoxes[] = []
    public availableCategories: Category[] = []
    public availableDesigns: Array<Array<Design>> = []
    public designsNotEmpty: number[] = []
    public productNames: ProductName = {
        '73': '72 ' + i18n.t('frameParts').toString(),
        '25': '24 ' + i18n.t('frameParts').toString()
    }
    public allCollages: CollageUsageAndInfo = {}
    public activeAssets: ActiveAsset = {}
    public imageRatios: Map<string, ImageRatio> = new Map()
    public maxCanvasSize = 400
    public croppingBorderInMm = 5
    public previewAssetId = ''
    public recoverPuzzleToggled = false
    public resetBoxToggled = false
    public persistedAssets: ActiveAsset = {}
    public persistedUsages: NameAndUsage = {}
    public persistedInfos: CollageUsageAndInfo = {}
    public stepsReseted = false
    public puzzleBoxUpdateState: PuzzleBoxUpdateState = PuzzleBoxUpdateState.UPDATED
    public deletingAsset = false
    public minImageSize = 1400
    public resetFrameDesignToggled = false
    public updatePreview = false

    @Mutation
    public SET_PARTS(parts: string) {
        let productId = getProductId(parts)
        if (productId)
            AppModule.SetProductID(productId)
        this.parts = parts
    }

    @Mutation
    public SET_GIFT_OPTION(giftOption: boolean) {
        this.giftOption = giftOption
    }

    @Mutation
    private SET_PUZZLE_FORMAT(format: PuzzleFormat) {
        this.format = format
    }

    @Mutation
    private SET_USER_FORMAT(format: string) {
        this.userFormat = format
    }

    @Mutation
    private SET_BOX_DESIGN_STATE(boxState: PuzzleBoxState) {
        this.boxState = boxState
    }

    @Mutation
    private SET_BOX_DESIGN(boxDesign: number) {
        this.boxDesign = boxDesign
    }

    @Mutation
    private SET_BOX_DESIGN_CATEGORY(boxDesignCategory: number) {
        this.boxDesignCategory = boxDesignCategory
    }

    @Mutation
    private SET_PUZZLE_IMAGE_ASSET_ID(imageAssetId: number) {
        this.puzzleImageAssetId = imageAssetId
    }

    @Mutation
    private SET_PUZZLE_IMAGE_BOUNDING_BOX(boundingBox: BoundingBox) {
        this.puzzleImageBoundingBox = boundingBox
    }

    @Mutation
    private SET_PUZZLE_IMAGE_ROTATION(rotation: number) {
        this.puzzleImageRotation = rotation
    }

    @Mutation
    private SET_RECOVER_PUZZLE_TOGGLED(toggled: boolean) {
        this.recoverPuzzleToggled = toggled
    }

    @Mutation
    private SET_RESET_BOX_TOGGLED(toggled: boolean) {
        this.resetBoxToggled = toggled
    }

    @Mutation
    private SET_SHOW_COLLAGE_UPLOAD(showUpload: boolean) {
        this.showOverlayUpload = showUpload
    }

    @Mutation
    private SET_COLLAGE_USAGE_INFO(info: CollageInfo) {
        window.console.log("set collage usage and info for active", info)
        let usageName = 'frameimage'
        this.allCollages[usageName] = info
    }

    @Mutation
    private SET_COLLAGE_USAGE_AND_INFO([usage, info]: [string, CollageInfo]) {
        window.console.log("set collage usage and info for " + usage, info)
        this.allCollages[usage] = info
    }

    @Mutation
    private PERSIST_USAGE_AND_INFO([usage, info]: [string, CollageInfo]) {
        this.persistedInfos[usage] = info
    }

    @Mutation
    private SET_NAME_AND_USAGE([usagename, usage]: [string, SingleAssetUsage]) {
        this.persistedUsages[usagename] = usage
    }

    @Mutation
    private SET_ACTIVE_ASSET([usage, asset]: [string, AssetFile]) {
        this.activeAssets[usage] = asset
        this.updatePreview = !this.updatePreview
    }

    @Mutation
    private SET_PERSISTED_ASSET([usage, asset]: [string, AssetFile]) {
        this.persistedAssets[usage] = asset
    }

    @Mutation
    private UNSET_ACTIVE_ASSET(usage: string) {
        Vue.delete(this.activeAssets, usage)
    }

    @Mutation
    private SET_AVAILABLE_PARTS(parts: ProductPart[]) {
        for (let i = 0; i < parts.length; i++) {
            let newValue = parts[i].part
            let newText = this.productNames[newValue] + ' ' + parts[i].price
            this.availableParts.push({ value: newValue, text: newText })
        }
    }

    @Mutation
    private SET_AVAILABLE_BOXES(boxes: ProductBoxes[]) {
        this.availableBoxes = boxes
    }

    @Mutation
    public SET_AVAILABLE_BOX_CATEGORIES(categories: Category[]) {
        this.availableCategories = categories
    }

    @Mutation
    public SET_AVAILABLE_DESIGNS(categoriesAndDesigns: Design[]) {
        this.availableDesigns.push(categoriesAndDesigns)
    }

    @Mutation
    public SET_DESIGN_NOT_EMPTY(id: number) {
        this.designsNotEmpty.push(id)
    }

    @Mutation
    public CLEAR_AVAILABLE_BOX_CATEGORIES(clear: boolean) {
        this.availableCategories = []
    }

    @Mutation
    public CLEAR_AVAILABLE_DESIGNS(clear: boolean) {
        this.availableDesigns = []
    }

    @Mutation
    public CLEAR_DESIGN_NOT_EMPTY(clear: boolean) {
        this.designsNotEmpty = []
    }

    @Mutation
    public SET_IMAGE_RATIOS(ratios: Map<string, ImageRatio>) {
        this.imageRatios = ratios
    }

    @Mutation
    public SET_PREVIEW_ASSET_ID(previewAssetId: string) {
        this.previewAssetId = previewAssetId
    }

    @Mutation
    public SET_STEPS_RESETED(stepsReseted: boolean) {
        this.stepsReseted = stepsReseted
    }

    @Mutation
    public CLEAR_PERSISTED_ASSET_DATA(key: string) {
        Vue.delete(this.persistedAssets, key)
        Vue.delete(this.persistedUsages, key)
        Vue.delete(this.persistedInfos, key)
    }

    @Mutation
    public CLEAR_ALL_COLLAGES(key: string) {
        Vue.delete(this.allCollages, key)
    }

    @Mutation
    public PUZZLE_BOX_UPDATE_STATE(puzzleBoxUpdateState: PuzzleBoxUpdateState) {
        this.puzzleBoxUpdateState = puzzleBoxUpdateState
    }

    @Mutation
    public SET_DELETING_ASSET(deletingAsset: boolean) {
        this.deletingAsset = deletingAsset
    }

    @Mutation
    private SET_RESET_FRAME_DESIGN_TOGGLED(toggled: boolean) {
        this.resetFrameDesignToggled = toggled
    }

    @Action({ commit: 'SET_PARTS' })
    public SetParts(parts: string) {
        return parts
    }

    @Action({ commit: 'SET_GIFT_OPTION' })
    public SetGiftOption(giftOption: boolean) {
        return giftOption
    }

    @Action({ commit: 'SET_PUZZLE_FORMAT' })
    public SetPuzzleFormat(format: PuzzleFormat) {
        return format
    }

    @Action({ commit: 'SET_USER_FORMAT' })
    public SetUserFormat(format: string) {
        return format
    }

    @Action({ commit: 'SET_BOX_DESIGN_STATE' })
    public SetBoxState(boxState: PuzzleBoxState) {
        return boxState
    }

    @Action({ commit: 'SET_BOX_DESIGN' })
    public SetBoxDesign(boxDesign: number) {
        return boxDesign
    }

    @Action({ commit: 'SET_BOX_DESIGN_CATEGORY' })
    public SetBoxDesignCategory(boxDesignCategory: number) {
        return boxDesignCategory
    }

    @Action({ commit: 'SET_PUZZLE_IMAGE_ASSET_ID' })
    public SetPuzzleImageAssetId(puzzleImageAssetId: number) {
        return puzzleImageAssetId
    }

    @Action({ commit: 'SET_PUZZLE_IMAGE_BOUNDING_BOX' })
    public SetPuzzleImageBoundingBox(boundingBox: BoundingBox) {
        return boundingBox
    }

    @Action({ commit: 'SET_PUZZLE_IMAGE_ROTATION' })
    public SetPuzzleImageRotation(rotation: number) {
        return rotation
    }

    @Action({ commit: 'SET_RECOVER_PUZZLE_TOGGLED' })
    public SetRecoverPuzzleToggled(toggled: boolean) {
        return toggled
    }

    @Action({ commit: 'SET_RESET_BOX_TOGGLED' })
    public SetResetBoxToggled(resetBoxToggled: boolean) {
        return resetBoxToggled
    }

    @Action({ commit: 'SET_SHOW_COLLAGE_UPLOAD' })
    public SetShowOverlayUpload(showUpload: boolean) {
        return showUpload
    }

    @Action({ commit: 'SET_COLLAGE_USAGE_INFO' })
    public SetCollageInfo(info: CollageInfo) {
        return info
    }

    @Action({ commit: 'SET_COLLAGE_USAGE_AND_INFO' })
    public SetCollageUsageAndInfo([usage, info]: [string, CollageInfo]) {
        return [usage, info]
    }

    @Action({ commit: 'PERSIST_USAGE_AND_INFO' })
    public PersistCollageUsageAndInfo([usage, info]: [string, CollageInfo]) {
        return [usage, info]
    }

    @Action({ commit: 'SET_NAME_AND_USAGE' })
    public PersistNameAndUsage([usagename, usage]: [string, SingleAssetUsage]) {
        return [usagename, usage]
    }

    @Action({ commit: 'SET_ACTIVE_ASSET' })
    public SetActiveAsset(asset: [String, AssetFile]) {
        return asset
    }

    @Action({ commit: 'UNSET_ACTIVE_ASSET' })
    public UnsetActiveAsset(usageName: String) {
        return usageName
    }

    @Action({ commit: 'SET_PERSISTED_ASSET' })
    public SetPersistedAsset(asset: [String, AssetFile]) {
        return asset
    }

    @Action({ commit: 'SET_AVAILABLE_PARTS' })
    public SetAvailableParts(parts: ProductPart[]) {
        return parts
    }

    @Action({ commit: 'SET_AVAILABLE_BOXES' })
    public SetAvailableBoxes(boxes: ProductBoxes[]) {
        return boxes
    }

    @Action({ commit: 'SET_AVAILABLE_BOX_CATEGORIES' })
    public SetAvailableBoxCategories(categories: Category[]) {
        return categories
    }

    @Action({ commit: 'SET_AVAILABLE_DESIGNS' })
    public SetAvailableDesigns(categoriesAndDesigns: Design[]) {
        return categoriesAndDesigns
    }

    @Action({ commit: 'SET_DESIGN_NOT_EMPTY' })
    public SetDesignNotEmpty(id: number) {
        return id
    }

    @Action({ commit: 'CLEAR_AVAILABLE_BOX_CATEGORIES' })
    public ClearCategories(clear: boolean) {
        return clear
    }

    @Action({ commit: 'CLEAR_AVAILABLE_DESIGNS' })
    public ClearDesigns(clear: boolean) {
        return clear
    }

    @Action({ commit: 'CLEAR_DESIGN_NOT_EMPTY' })
    public ClearNotEmptyDesigns(clear: boolean) {
        return clear
    }

    @Action({ commit: 'SET_IMAGE_RATIOS' })
    public SetImageRatios(ratios: Map<string, ImageRatio>) {
        return ratios
    }

    @Action({ commit: 'SET_PREVIEW_ASSET_ID' })
    public SetPreviewAssetId(previewAssetId: string) {
        return previewAssetId
    }

    @Action({ commit: 'SET_STEPS_RESETED' })
    public SetStepsReseted(stepsReseted: boolean) {
        return stepsReseted
    }

    @Action({ commit: 'CLEAR_PERSISTED_ASSET_DATA' })
    public ClearPersistedAssetData(key: string) {
        return key
    }

    @Action({ commit: 'CLEAR_ALL_COLLAGES' })
    public ClearAllCollages(key: string) {
        return key
    }

    @Action({ commit: 'PUZZLE_BOX_UPDATE_STATE' })
    public SetPuzzleBoxUpdateState(puzzleBoxUpdateState: PuzzleBoxUpdateState) {
        return puzzleBoxUpdateState
    }

    @Action({ commit: 'SET_DELETING_ASSET' })
    public SetDeletingAsset(deletingAsset: boolean) {
        return deletingAsset
    }

    @Action({ commit: 'SET_RESET_FRAME_DESIGN_TOGGLED' })
    public SetResetFrameDesignToggled(resetBoxToggled: boolean) {
        return resetBoxToggled
    }

    get showFrame(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "design-frame" && !AppModule.showMenu
    }

    get showPuzzle(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "design-puzzle" && !AppModule.showMenu
    }

    get showImageUpload(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "puzzle-image-upload"
    }

    get showImagePreview(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName === "puzzle-image-upload" && !AppModule.showMenu
    }

    get customPuzzleTransfer(): AddCustomPuzzleRequest {
        let customPuzzle = new FramePuzzleTransfer(AppModule.session.sessionId, FramePuzzleModule.parts, PuzzleFormat[FramePuzzleModule.format], this.boxDesign, + this.previewAssetId)
        customPuzzle.gift = FramePuzzleModule.giftOption

        let addCustomPuzzleRequest: AddCustomPuzzleRequest = { customPuzzle: customPuzzle }
        return addCustomPuzzleRequest;
    }

    get isLandscapeFormat(): boolean {
        return this.format == PuzzleFormat.LANDSCAPE
    }

    get showToolbar(): boolean {
        let step = AppModule.steps[AppModule.currentStepIndex]
        return step.componentName == "design-puzzle" && !AppModule.showMenu
    }

    get imageEditorWidth() {
        let ratio = this.imageRatios.get(this.parts)
        if (this.format === PuzzleFormat.LANDSCAPE) {
            return this.maxCanvasSize
        } else if (ratio && ratio.width && ratio.height) {
            let scale = this.maxCanvasSize / ratio.width
            return Math.round(ratio.height * scale)
        }
        return 0
    }

    get imageEditorHeight() {
        let ratio = this.imageRatios.get(this.parts)
        if (this.format !== PuzzleFormat.LANDSCAPE) {
            return this.maxCanvasSize
        } else if (ratio && ratio.width && ratio.height) {
            let scale = this.maxCanvasSize / ratio.width
            return Math.round(ratio.height * scale)
        }
        return 0
    }

    get frameStepComplete() {
        return AppModule.steps[2].completed
    }
}

export const FramePuzzleModule = getModule(FramePuzzleImpl)
